// frontend/src/modules/admin/GestionStatutsCommandesV2.jsx
// ═══════════════════════════════════════════════════════════════════════════
// VERSION 2 - INTERFACE ERGONOMIQUE SIMPLIFIÉE
// ═══════════════════════════════════════════════════════════════════════════
// Papy 73+ - Farm-Booking 2026
// 
// MODIFICATIONS PAR RAPPORT À V1 :
// - Workflow simplifié : En Attente → Confirmées → En Préparation → Prêtes → Retirées
// - "En Récolte" conservé en BDD mais bypassé dans l'UI
// - Actions groupées avec checkboxes
// - Zone détail ligne active
// - Sauts de statut possibles
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { 
  Package, CheckCircle, Truck, ShoppingBag, XCircle, Archive, 
  AlertCircle, Edit, Scale, Leaf, ChevronDown, ChevronUp, Users,
  Check, Square, CheckSquare, ArrowRight, Printer, RefreshCw
} from 'lucide-react';
import ModeTestActions from './ModeTestActions';
import { useMessageBox } from '../../../components/MessageBox';

const API_BASE = '/api';

const GestionStatutsCommandesV2 = ({ config }) => {
  // ════════════════════════════════════════════════════════════════════════
  // HOOKS & ÉTATS
  // ════════════════════════════════════════════════════════════════════════
  const { showMessage, showConfirm } = useMessageBox();

  // Données réservations
  const [reservations, setReservations] = useState({
    en_attente: [],
    confirmee: [],
    en_recolte: [],      // Conservé mais non affiché
    en_preparation: [],
    prete: [],
    retiree: [],
    annulee: [],
    non_retiree: []
  });
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPeriode, setSelectedPeriode] = useState(null);
  const [periodes, setPeriodes] = useState([]);

  // 🆕 NOUVEAUX ÉTATS POUR INTERFACE V2
  const [filtreStatut, setFiltreStatut] = useState('en_attente');  // Filtre actif
  const [selectedResas, setSelectedResas] = useState([]);          // Checkboxes sélectionnées
  const [ligneActive, setLigneActive] = useState(null);            // Ligne sélectionnée pour détail
  const [selectAll, setSelectAll] = useState(false);               // Tout sélectionner

  // États modales existantes (conservées)
  const [showModalePesee, setShowModalePesee] = useState(false);
  const [resaEnCours, setResaEnCours] = useState(null);
  const [lignesProduits, setLignesProduits] = useState([]);
  const [loadingPesee, setLoadingPesee] = useState(false);

  const [showModaleModif, setShowModaleModif] = useState(false);
  const [resaModif, setResaModif] = useState(null);
  const [produitsModif, setProduitsModif] = useState([]);
  const [loadingModif, setLoadingModif] = useState(false);
  const [categories, setCategories] = useState([]);
  const [produitsCategorie, setProduitsCategorie] = useState([]);
  const [categorieSelectionnee, setCategorieSelectionnee] = useState('');
  const [produitSelectionne, setProduitSelectionne] = useState('');
  const [notesFermier, setNotesFermier] = useState('');
  const [notifierClient, setNotifierClient] = useState(true);

  // Config ferme
  const [configFerme, setConfigFerme] = useState({});

  // ════════════════════════════════════════════════════════════════════════
  // DÉFINITION DES STATUTS (V2 - SIMPLIFIÉ)
  // ════════════════════════════════════════════════════════════════════════
  // "en_recolte" existe en BDD mais n'est pas dans cette liste d'affichage
  const statutsAffiches = [
    {
      id: 'en_attente',
      titre: 'En Attente',
      icon: AlertCircle,
      color: 'bg-yellow-100 border-yellow-400',
      textColor: 'text-yellow-800',
      bgHeader: 'bg-yellow-500',
      // Actions possibles depuis ce statut
      actionsPossibles: ['confirmee', 'en_preparation', 'annulee']
    },
    {
      id: 'confirmee',
      titre: 'Confirmées',
      icon: CheckCircle,
      color: 'bg-blue-100 border-blue-400',
      textColor: 'text-blue-800',
      bgHeader: 'bg-blue-500',
      actionsPossibles: ['en_preparation', 'prete', 'annulee']
    },
    {
      id: 'en_preparation',
      titre: 'En Préparation',
      icon: Package,
      color: 'bg-orange-100 border-orange-400',
      textColor: 'text-orange-800',
      bgHeader: 'bg-orange-500',
      actionsPossibles: ['prete', 'annulee']
    },
    {
      id: 'prete',
      titre: 'Prêtes',
      icon: ShoppingBag,
      color: 'bg-green-100 border-green-400',
      textColor: 'text-green-800',
      bgHeader: 'bg-green-500',
      actionsPossibles: ['retiree', 'annulee']
    },
    {
      id: 'retiree',
      titre: 'Retirées',
      icon: Archive,
      color: 'bg-gray-100 border-gray-400',
      textColor: 'text-gray-700',
      bgHeader: 'bg-gray-500',
      actionsPossibles: []  // Terminé
    }
  ];

  // Labels pour les boutons d'action
  const labelsActions = {
    confirmee: '✓ Confirmer',
    en_preparation: '📦 Préparation',
    prete: '✅ Prête',
    retiree: '🏠 Retirée',
    annulee: '✕ Annuler'
  };

  // ════════════════════════════════════════════════════════════════════════
  // USE EFFECTS
  // ════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    loadPeriodes();
    loadConfigFerme();
  }, []);

  useEffect(() => {
    loadReservations();
  }, [selectedPeriode]);

  // Reset sélection quand on change de filtre
  useEffect(() => {
    setSelectedResas([]);
    setSelectAll(false);
    setLigneActive(null);
  }, [filtreStatut]);

  // ════════════════════════════════════════════════════════════════════════
  // FONCTIONS DE CHARGEMENT
  // ════════════════════════════════════════════════════════════════════════
  const loadPeriodes = async () => {
    try {
      const response = await fetch(`${API_BASE}/reservations/periodes`);
      const data = await response.json();
      if (data.success) {
        setPeriodes(data.data || []);
        const active = data.data.find(p => p.statut === 'en_cours');
        if (active) {
          setSelectedPeriode(active.id);
        }
      }
    } catch (err) {
      console.error('Erreur chargement périodes:', err);
    }
  };

  const loadConfigFerme = async () => {
    try {
      const response = await fetch(`${API_BASE}/configuration`);
      const data = await response.json();
      const configObj = {};
      const items = data.data || data || [];
      items.forEach(item => { configObj[item.param_name] = item.param_value; });
      setConfigFerme(configObj);
    } catch (err) {
      console.error('Erreur config ferme:', err);
    }
  };

  const loadReservations = async () => {
    setLoading(true);
    try {
      const url = selectedPeriode 
        ? `${API_BASE}/gestion-statuts/par-statut/${selectedPeriode}`
        : `${API_BASE}/gestion-statuts/par-statut`;
        
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        // 🆕 Fusionner "en_recolte" dans "en_preparation" pour l'affichage
        const dataFusionnee = { ...data.data };
        dataFusionnee.en_preparation = [
          ...(data.data.en_recolte || []),
          ...(data.data.en_preparation || [])
        ];
        setReservations(dataFusionnee);
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Erreur chargement réservations:', err);
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // GESTION DES SÉLECTIONS
  // ════════════════════════════════════════════════════════════════════════
  const resasFiltrees = reservations[filtreStatut] || [];

  const toggleSelectResa = (resaId) => {
    setSelectedResas(prev => {
      if (prev.includes(resaId)) {
        return prev.filter(id => id !== resaId);
      } else {
        return [...prev, resaId];
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedResas([]);
    } else {
      setSelectedResas(resasFiltrees.map(r => r.id));
    }
    setSelectAll(!selectAll);
  };

  const selectLigne = (resa) => {
    setLigneActive(resa);
  };

  // ════════════════════════════════════════════════════════════════════════
  // CHANGEMENT DE STATUT (UNITAIRE ET GROUPÉ)
  // ════════════════════════════════════════════════════════════════════════
  const changerStatut = async (reservationId, nouveauStatut) => {
    const confirmResult = await showConfirm(
      `Confirmer le passage vers "${labelsActions[nouveauStatut] || nouveauStatut}" ?`, 
      'warning'
    );
    if (!confirmResult) return;

    try {
      const response = await fetch(`${API_BASE}/gestion-statuts/changer-statut/${reservationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nouveau_statut: nouveauStatut })
      });

      const data = await response.json();
      
      if (data.success) {
        showMessage('success', data.message);
        setTimeout(() => loadReservations(), 500);
      } else {
        showMessage('error', data.message);
      }
    } catch (err) {
      console.error('Erreur changement statut:', err);
      showMessage('error', 'Erreur lors du changement de statut');
    }
  };

  // 🆕 ACTION GROUPÉE
  const changerStatutGroupe = async (nouveauStatut) => {
    if (selectedResas.length === 0) {
      showMessage('warning', 'Aucune réservation sélectionnée');
      return;
    }

    const confirmResult = await showConfirm(
      `Passer ${selectedResas.length} réservation(s) vers "${labelsActions[nouveauStatut] || nouveauStatut}" ?`,
      'warning'
    );
    if (!confirmResult) return;

    let success = 0;
    let errors = 0;

    for (const resaId of selectedResas) {
      try {
        const response = await fetch(`${API_BASE}/gestion-statuts/changer-statut/${resaId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nouveau_statut: nouveauStatut })
        });
        const data = await response.json();
        if (data.success) {
          success++;
        } else {
          errors++;
        }
      } catch (err) {
        errors++;
      }
    }

    if (success > 0) {
      showMessage('success', `${success} réservation(s) modifiée(s)`);
    }
    if (errors > 0) {
      showMessage('warning', `${errors} erreur(s) rencontrée(s)`);
    }

    setSelectedResas([]);
    setSelectAll(false);
    loadReservations();
  };

  // ════════════════════════════════════════════════════════════════════════
  // MODALE PESÉE (CONSERVÉE DE V1)
  // ════════════════════════════════════════════════════════════════════════
  const ouvrirModalePesee = async (resa) => {
    setResaEnCours(resa);
    setLoadingPesee(true);
    setShowModalePesee(true);

    try {
      const response = await fetch(`${API_BASE}/reservations/${resa.id}/lignes`);
      const data = await response.json();
      
      if (data.success) {
        const lignes = data.data.map(ligne => ({
          ...ligne,
          quantite_livree_tmp: ligne.quantite_livree || ligne.quantite_reservee
        }));
        setLignesProduits(lignes);
      }
    } catch (err) {
      console.error('Erreur chargement lignes:', err);
      showMessage('error', 'Erreur chargement des produits');
    } finally {
      setLoadingPesee(false);
    }
  };

  const sauvegarderPesee = async () => {
    const confirmResult = await showConfirm('Sauvegarder les quantités pesées ?', 'warning');
    if (!confirmResult) return;

    setLoadingPesee(true);

    try {
      for (const ligne of lignesProduits) {
        await fetch(`${API_BASE}/reservations/lignes/${ligne.id}/pesee`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quantite_livree: ligne.quantite_livree_tmp,
            notes_pesee: `Pesée du ${new Date().toLocaleDateString('fr-FR')}`
          })
        });
      }

      showMessage('success', 'Pesée sauvegardée !');
      setShowModalePesee(false);
      loadReservations();
      
    } catch (err) {
      console.error('Erreur sauvegarde pesée:', err);
      showMessage('error', 'Erreur sauvegarde');
    } finally {
      setLoadingPesee(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // MODALE MODIFICATION (CONSERVÉE DE V1)
  // ════════════════════════════════════════════════════════════════════════
  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/reservations/categories`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  const loadProduitsCategorie = async (categorieId) => {
    try {
      const response = await fetch(`${API_BASE}/reservations/produits/par-categorie/${categorieId}`);
      const data = await response.json();
      if (data.success) {
        setProduitsCategorie(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement produits:', err);
    }
  };

  const ouvrirModaleModif = async (resa) => {
    setResaModif(resa);
    setLoadingModif(true);
    setShowModaleModif(true);
    setCategorieSelectionnee('');
    setProduitSelectionne('');
    setNotesFermier(resa.notes_fermier || '');
    setNotifierClient(true);
    
    await loadCategories();

    try {
      const response = await fetch(`${API_BASE}/reservations/${resa.id}/lignes`);
      const data = await response.json();
      
      if (data.success) {
        setProduitsModif(data.data);
      }
    } catch (err) {
      console.error('Erreur chargement produits:', err);
      showMessage('error', 'Erreur chargement');
    } finally {
      setLoadingModif(false);
    }
  };

  const ajouterProduit = () => {
    if (!produitSelectionne) {
      showMessage('warning', 'Veuillez sélectionner un produit');
      return;
    }
    
    const produit = produitsCategorie.find(p => p.code_produit === produitSelectionne);
    if (!produit) return;
    
    const dejaPresent = produitsModif.find(p => p.code_produit === produit.code_produit);
    if (dejaPresent) {
      showMessage('warning', 'Ce produit est déjà dans la réservation');
      return;
    }
    
    const nouvelleLigne = {
      id: null,
      code_produit: produit.code_produit,
      designation_produit: produit.designation,
      unite: produit.unite,
      quantite_reservee: 1,
      prix_unitaire: produit.prix_ttc,
      montant_estime: produit.prix_ttc
    };
    
    setProduitsModif([...produitsModif, nouvelleLigne]);
    setProduitSelectionne('');
  };

  const modifierQuantite = (index, nouvelleQte) => {
    const nouvelles = [...produitsModif];
    nouvelles[index].quantite_reservee = parseFloat(nouvelleQte) || 0;
    nouvelles[index].montant_estime = nouvelles[index].quantite_reservee * nouvelles[index].prix_unitaire;
    setProduitsModif(nouvelles);
  };

  const modifierPrix = (index, nouveauPrix) => {
    const nouvelles = [...produitsModif];
    nouvelles[index].prix_unitaire = parseFloat(nouveauPrix) || 0;
    nouvelles[index].montant_estime = nouvelles[index].quantite_reservee * nouvelles[index].prix_unitaire;
    setProduitsModif(nouvelles);
  };

  const supprimerLigne = (index) => {
    const nouvelles = produitsModif.filter((_, i) => i !== index);
    setProduitsModif(nouvelles);
  };

  const calculerTotal = () => {
    return produitsModif.reduce((sum, p) => {
      return sum + (parseFloat(p.quantite_reservee) * parseFloat(p.prix_unitaire));
    }, 0);
  };

  const sauvegarderModifications = async () => {
    if (produitsModif.length === 0) {
      showMessage('warning', 'La réservation doit contenir au moins 1 produit');
      return;
    }
    
    const messageConfirm = notifierClient 
      ? 'Sauvegarder les modifications ?\n\n📧 Un email sera envoyé au client.'
      : 'Sauvegarder les modifications ?\n\n⚠️ Le client ne sera PAS notifié.';
    
    const confirmResult = await showConfirm(messageConfirm, 'warning');
    if (!confirmResult) return;
    
    setLoadingModif(true);
    
    try {
      const response = await fetch(`${API_BASE}/reservations/${resaModif.id}/lignes/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lignes: produitsModif.map(p => ({
            id: p.id,
            code_produit: p.code_produit,
            quantite_reservee: p.quantite_reservee,
            prix_unitaire: p.prix_unitaire
          })),
          notes_fermier: notesFermier,
          notifier_client: notifierClient
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage('success', 'Modifications enregistrées !');
        setShowModaleModif(false);
        loadReservations();
      } else {
        showMessage('error', data.message || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      showMessage('error', 'Erreur lors de la sauvegarde');
    } finally {
      setLoadingModif(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  // IMPRESSION
  // ════════════════════════════════════════════════════════════════════════
  const imprimerListe = () => {
    const statutConfig = statutsAffiches.find(s => s.id === filtreStatut);
    const periodeNom = periodes.find(p => p.id === selectedPeriode)?.nom || 'Toutes périodes';
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Liste ${statutConfig?.titre || filtreStatut}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
          h1 { color: #2d5a27; border-bottom: 2px solid #2d5a27; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #2d5a27; color: white; }
          tr:nth-child(even) { background: #f9f9f9; }
          .montant { text-align: right; font-weight: bold; }
          .header-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <h1>🌱 ${configFerme.nom_ferme || 'LA FERME DU PETIT ESSART'}</h1>
        <div class="header-info">
          <div><strong>Liste : ${statutConfig?.titre || filtreStatut}</strong></div>
          <div><strong>Période : ${periodeNom}</strong></div>
          <div>${new Date().toLocaleDateString('fr-FR')} ${new Date().toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'})}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>N° Réservation</th>
              <th>Client</th>
              <th>Code Client</th>
              <th>Statut</th>
              <th class="montant">Montant</th>
              <th>Date retrait</th>
            </tr>
          </thead>
          <tbody>
            ${resasFiltrees.map(r => `
              <tr>
                <td>${r.numero_reservation || '-'}</td>
                <td>${r.nom_prenom || '-'}</td>
                <td>${r.code_client || '-'}</td>
                <td>${statutConfig?.titre || r.statut}</td>
                <td class="montant">${parseFloat(r.montant_reel || r.montant_estime || 0).toFixed(2)} €</td>
                <td>${r.date_retrait_prevue ? new Date(r.date_retrait_prevue).toLocaleDateString('fr-FR') : '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <p style="margin-top: 20px;"><strong>Total : ${resasFiltrees.length} réservation(s)</strong></p>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // ════════════════════════════════════════════════════════════════════════
  // UTILITAIRES
  // ════════════════════════════════════════════════════════════════════════
  const formatMontant = (m) => parseFloat(m || 0).toFixed(2) + ' €';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '-';
  const formatNumero = (num) => num ? num.replace(/-\d{8}$/, '') : num;

  const statutActuel = statutsAffiches.find(s => s.id === filtreStatut);

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-12 w-12 border-4 border-green-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ══════════════════════════════════════════════════════════════════
          EN-TÊTE : Sélecteur période + titre
      ══════════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">📋 Gestion des Réservations</h1>
            <p className="text-sm text-gray-500">Interface simplifiée - Actions groupées</p>
          </div>
          
          <div className="flex items-center gap-4">
            <select
              value={selectedPeriode || ''}
              onChange={(e) => setSelectedPeriode(e.target.value ? parseInt(e.target.value) : null)}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="">Toutes les périodes</option>
              {periodes.map(p => (
                <option key={p.id} value={p.id}>
                  {p.nom} {p.statut === 'en_cours' ? '(active)' : ''}
                </option>
              ))}
            </select>
            
            <button 
              onClick={loadReservations}
              className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
            >
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          FILTRES PAR STATUT (Bandeau horizontal)
      ══════════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          {statutsAffiches.map(statut => {
            const Icon = statut.icon;
            const count = (reservations[statut.id] || []).length;
            const isActive = filtreStatut === statut.id;
            
            return (
              <button
                key={statut.id}
                onClick={() => setFiltreStatut(statut.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                  isActive 
                    ? `${statut.color} ${statut.textColor} border-current font-bold shadow-md` 
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{statut.titre}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  isActive ? 'bg-white/50' : 'bg-gray-200'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          ZONE DÉTAIL LIGNE ACTIVE
      ══════════════════════════════════════════════════════════════════ */}
      {ligneActive && (
        <div className={`rounded-lg shadow p-4 border-2 ${statutActuel?.color || 'bg-gray-100'}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Infos de la réservation */}
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <div className="text-xs text-gray-500">N° Réservation</div>
                <div className="font-bold text-gray-800">{formatNumero(ligneActive.numero_reservation)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Client</div>
                <div className="font-medium">{ligneActive.nom_prenom}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Code</div>
                <div className="font-mono text-sm">{ligneActive.code_client}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Statut</div>
                <div className={`font-medium ${statutActuel?.textColor}`}>{statutActuel?.titre}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Montant</div>
                <div className="font-bold text-green-600">{formatMontant(ligneActive.montant_reel || ligneActive.montant_estime)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Date retrait</div>
                <div>{formatDate(ligneActive.date_retrait_prevue)}</div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => ouvrirModaleModif(ligneActive)}
                className="flex items-center gap-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              >
                <Edit className="h-4 w-4" /> Modifier
              </button>
              
              {filtreStatut === 'en_preparation' && (
                <button
                  onClick={() => ouvrirModalePesee(ligneActive)}
                  className="flex items-center gap-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm"
                >
                  <Scale className="h-4 w-4" /> Peser
                </button>
              )}

              {statutActuel?.actionsPossibles.filter(a => a !== 'annulee').slice(0, 1).map(action => (
                <button
                  key={action}
                  onClick={() => changerStatut(ligneActive.id, action)}
                  className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <ArrowRight className="h-4 w-4" /> {labelsActions[action]}
                </button>
              ))}

              {statutActuel?.actionsPossibles.includes('annulee') && (
                <button
                  onClick={() => changerStatut(ligneActive.id, 'annulee')}
                  className="flex items-center gap-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                >
                  <XCircle className="h-4 w-4" /> Annuler
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          TABLE DES RÉSERVATIONS AVEC CHECKBOXES
      ══════════════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Header du tableau */}
        <div className={`${statutActuel?.bgHeader || 'bg-gray-500'} text-white px-4 py-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            {statutActuel && <statutActuel.icon className="h-5 w-5" />}
            <span className="font-bold">{statutActuel?.titre || filtreStatut}</span>
            <span className="bg-white/20 px-2 py-0.5 rounded text-sm">{resasFiltrees.length} réservation(s)</span>
          </div>
          
          <button
            onClick={imprimerListe}
            className="flex items-center gap-1 px-3 py-1 bg-white/20 rounded hover:bg-white/30 text-sm"
          >
            <Printer className="h-4 w-4" /> Imprimer
          </button>
        </div>

        {resasFiltrees.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Aucune réservation dans ce statut</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 w-10">
                    <button onClick={toggleSelectAll} className="p-1 hover:bg-gray-200 rounded">
                      {selectAll ? (
                        <CheckSquare className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Square className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </th>
                  <th className="text-left p-3 font-semibold">N° Réservation</th>
                  <th className="text-left p-3 font-semibold">Code Client</th>
                  <th className="text-left p-3 font-semibold">Client</th>
                  <th className="text-left p-3 font-semibold">Statut</th>
                  <th className="text-right p-3 font-semibold">Montant</th>
                  <th className="text-left p-3 font-semibold">Date retrait</th>
                </tr>
              </thead>
              <tbody>
                {resasFiltrees.map((resa, idx) => {
                  const isSelected = selectedResas.includes(resa.id);
                  const isLigneActive = ligneActive?.id === resa.id;
                  
                  return (
                    <tr 
                      key={resa.id}
                      onClick={() => selectLigne(resa)}
                      className={`border-b cursor-pointer transition-colors ${
                        isLigneActive 
                          ? 'bg-blue-100 border-blue-300' 
                          : isSelected 
                            ? 'bg-yellow-50' 
                            : idx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={() => toggleSelectResa(resa.id)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          {isSelected ? (
                            <CheckSquare className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Square className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="p-3 font-mono font-medium">{formatNumero(resa.numero_reservation)}</td>
                      <td className="p-3 font-mono text-gray-600">{resa.code_client}</td>
                      <td className="p-3 font-medium">{resa.nom_prenom}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${statutActuel?.color} ${statutActuel?.textColor}`}>
                          {statutActuel?.titre}
                        </span>
                      </td>
                      <td className="p-3 text-right font-bold text-green-600">
                        {formatMontant(resa.montant_reel || resa.montant_estime)}
                      </td>
                      <td className="p-3">{formatDate(resa.date_retrait_prevue)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          ACTIONS GROUPÉES (Affichées si sélection active)
      ══════════════════════════════════════════════════════════════════ */}
      {selectedResas.length > 0 && statutActuel?.actionsPossibles.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 border-2 border-blue-300">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-blue-600" />
              <span className="font-bold text-blue-800">
                {selectedResas.length} réservation(s) sélectionnée(s)
              </span>
            </div>
            
            <div className="flex-1" />
            
            <div className="flex flex-wrap gap-2">
              <span className="text-gray-500 self-center mr-2">Action groupée :</span>
              
              {statutActuel.actionsPossibles.filter(a => a !== 'annulee').map(action => (
                <button
                  key={action}
                  onClick={() => changerStatutGroupe(action)}
                  className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  {labelsActions[action]}
                </button>
              ))}
              
              {statutActuel.actionsPossibles.includes('annulee') && (
                <button
                  onClick={() => changerStatutGroupe('annulee')}
                  className="flex items-center gap-1 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium"
                >
                  {labelsActions.annulee}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODALE DE PESÉE
      ══════════════════════════════════════════════════════════════════ */}
      {showModalePesee && resaEnCours && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-purple-600 text-white p-6 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">⚖️ Pesée des produits</h2>
                  <p className="text-purple-100">Réservation : {resaEnCours.numero_reservation}</p>
                  <p className="text-purple-100">Client : {resaEnCours.nom_prenom}</p>
                </div>
                <button onClick={() => setShowModalePesee(false)} className="text-white hover:text-purple-200 text-2xl">✕</button>
              </div>
            </div>

            <div className="p-6">
              {loadingPesee ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <div className="text-gray-500">Chargement...</div>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {lignesProduits.map((ligne, index) => {
                      const montantEstime = parseFloat(ligne.quantite_reservee) * parseFloat(ligne.prix_unitaire);
                      const montantReel = parseFloat(ligne.quantite_livree_tmp) * parseFloat(ligne.prix_unitaire);
                      const ecart = montantReel - montantEstime;

                      return (
                        <div key={ligne.id} className="border rounded-lg p-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h3 className="font-bold text-gray-900 mb-2">{ligne.designation_produit}</h3>
                              <div className="text-sm text-gray-600 space-y-1">
                                <div>Prix unitaire : {parseFloat(ligne.prix_unitaire).toFixed(2)}€ / {ligne.unite}</div>
                                <div>Quantité réservée : {ligne.quantite_reservee} {ligne.unite}</div>
                                <div>Montant estimé : {montantEstime.toFixed(2)}€</div>
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Quantité pesée ({ligne.unite}) :
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                value={ligne.quantite_livree_tmp}
                                onChange={(e) => {
                                  const nouvelles = [...lignesProduits];
                                  nouvelles[index].quantite_livree_tmp = parseFloat(e.target.value) || 0;
                                  setLignesProduits(nouvelles);
                                }}
                                className="w-full border rounded-lg px-3 py-2 text-lg font-bold"
                              />
                              <div className="mt-2 flex justify-between text-sm">
                                <span>Montant réel : <strong className="text-green-600">{montantReel.toFixed(2)}€</strong></span>
                                <span className={ecart >= 0 ? 'text-green-600' : 'text-red-600'}>
                                  {ecart >= 0 ? '+' : ''}{ecart.toFixed(2)}€
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 flex justify-between items-center">
                    <div className="text-lg">
                      <strong>Total réel : </strong>
                      <span className="text-2xl font-bold text-green-600">
                        {lignesProduits.reduce((sum, l) => sum + (parseFloat(l.quantite_livree_tmp) * parseFloat(l.prix_unitaire)), 0).toFixed(2)}€
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowModalePesee(false)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={sauvegarderPesee}
                        disabled={loadingPesee}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {loadingPesee ? 'Sauvegarde...' : '✓ Valider la pesée'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          MODALE DE MODIFICATION
      ══════════════════════════════════════════════════════════════════ */}
      {showModaleModif && resaModif && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-blue-600 text-white p-6 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">✏️ Modifier la réservation</h2>
                  <p className="text-blue-100">{resaModif.numero_reservation}</p>
                  <p className="text-blue-100">Client : {resaModif.nom_prenom}</p>
                </div>
                <button onClick={() => setShowModaleModif(false)} className="text-white hover:text-blue-200 text-2xl">✕</button>
              </div>
            </div>

            <div className="p-6">
              {loadingModif ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <div className="text-gray-500">Chargement...</div>
                </div>
              ) : (
                <>
                  {/* Produits existants */}
                  <div className="mb-6">
                    <h3 className="font-bold text-gray-800 mb-3">Produits de la réservation</h3>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left p-2">Produit</th>
                            <th className="text-right p-2 w-24">Quantité</th>
                            <th className="text-right p-2 w-24">Prix unit.</th>
                            <th className="text-right p-2 w-24">Total</th>
                            <th className="p-2 w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {produitsModif.map((prod, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="p-2">{prod.designation_produit}</td>
                              <td className="p-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={prod.quantite_reservee}
                                  onChange={(e) => modifierQuantite(idx, e.target.value)}
                                  className="w-full border rounded px-2 py-1 text-right"
                                />
                              </td>
                              <td className="p-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={prod.prix_unitaire}
                                  onChange={(e) => modifierPrix(idx, e.target.value)}
                                  className="w-full border rounded px-2 py-1 text-right"
                                />
                              </td>
                              <td className="p-2 text-right font-bold text-green-600">
                                {(parseFloat(prod.quantite_reservee) * parseFloat(prod.prix_unitaire)).toFixed(2)}€
                              </td>
                              <td className="p-2">
                                <button
                                  onClick={() => supprimerLigne(idx)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-100">
                          <tr>
                            <td colSpan="3" className="p-2 font-bold text-right">TOTAL</td>
                            <td className="p-2 text-right font-bold text-green-600 text-lg">{calculerTotal().toFixed(2)}€</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* Ajouter un produit */}
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-3">➕ Ajouter un produit</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <select
                        value={categorieSelectionnee}
                        onChange={(e) => {
                          setCategorieSelectionnee(e.target.value);
                          if (e.target.value) loadProduitsCategorie(e.target.value);
                        }}
                        className="border rounded px-3 py-2"
                      >
                        <option value="">-- Catégorie --</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.nom}</option>
                        ))}
                      </select>
                      <select
                        value={produitSelectionne}
                        onChange={(e) => setProduitSelectionne(e.target.value)}
                        className="border rounded px-3 py-2"
                        disabled={!categorieSelectionnee}
                      >
                        <option value="">-- Produit --</option>
                        {produitsCategorie.map(prod => (
                          <option key={prod.code_produit} value={prod.code_produit}>
                            {prod.designation} - {prod.prix_ttc}€/{prod.unite}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={ajouterProduit}
                        disabled={!produitSelectionne}
                        className="bg-blue-600 text-white rounded px-4 py-2 hover:bg-blue-700 disabled:opacity-50"
                      >
                        Ajouter
                      </button>
                    </div>
                  </div>

                  {/* Notes fermier */}
                  <div className="mb-6">
                    <label className="block font-bold text-gray-800 mb-2">Notes fermier</label>
                    <textarea
                      value={notesFermier}
                      onChange={(e) => setNotesFermier(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 h-20"
                      placeholder="Notes internes..."
                    />
                  </div>

                  {/* Notification client */}
                  <div className="mb-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifierClient}
                        onChange={(e) => setNotifierClient(e.target.checked)}
                        className="h-5 w-5"
                      />
                      <span>📧 Envoyer un email au client pour l'informer des modifications</span>
                    </label>
                  </div>

                  {/* Boutons */}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowModaleModif(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={sauvegarderModifications}
                      disabled={loadingModif}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loadingModif ? 'Sauvegarde...' : '✓ Enregistrer'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mode test (si activé) */}
      {config?.modeTest && <ModeTestActions />}
    </div>
  );
};

export default GestionStatutsCommandesV2;